// Este código va en tu archivo api.ts

import { Product, Category, ScheduleCall, NewProduct, NewCategory, SparePart, ProductSparePart } from '@/types/admin';
import { supabase } from '@/lib/supabase';

// URL base para las Edge Functions, idéntica a la usada en marketplaceAPI.ts
const API_URL = 'https://jlufgkgssnxumguvknhc.functions.supabase.co';
const SUPABASE_STORAGE_URL = 'https://jlufgkgssnxumguvknhc.supabase.co/storage/v1';

// Token de autenticación fijo para las Edge Functions (similar a marketplaceAPI)
const API_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpsZWZna2dzc254dW1ndXZrbmhjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MDk4MjI0MDAsImV4cCI6MjAyNTM5ODQwMH0.dkF2QHwHu2Rl2yZQDVTqS_FWzOhGBZGJXYZKUcF0QXE';

export async function apiRequest<T>(method: string, endpoint: string, data?: any): Promise<T> {
  // Usar exclusivamente el API_KEY fijo sin intentar obtener la sesión
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    "Authorization": `Bearer ${API_KEY}`
  };

  console.log(`Enviando solicitud ${method} a ${endpoint} con API_KEY fijo`);

  const options: RequestInit = {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  };

  try {
    const response = await fetch(`${API_URL}${endpoint}`, options);

    // Imprimir información de la respuesta para depuración
    console.log(`Respuesta de ${endpoint}: Status ${response.status}`);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { message: `Error ${response.status}: ${response.statusText}` };
      }

      // Manejar específicamente el error 401
      if (response.status === 401) {
        console.error("Error de autenticación 401:", endpoint);
        throw new Error(`Error de autenticación: ${errorData.message || errorData.error || "Tu sesión ha expirado, inicia sesión nuevamente"}`);
      }

      throw new Error(errorData.message || errorData.error || "Error en la petición");
    }

    const responseData = await response.json();

    // Normalizar datos para endpoint de recambios
    if (endpoint === "/admin/spare-parts" || endpoint.startsWith("/admin/spare-parts/") ||
        endpoint === "/admin/product-spare-parts" || endpoint.startsWith("/admin/product-spare-parts/") ||
        endpoint === "/spare-parts" || endpoint.startsWith("/spare-parts/") ||
        endpoint === "/product-spare-parts" || endpoint.startsWith("/product-spare-parts/")) {
      if (Array.isArray(responseData)) {
        return responseData.map(transformSnakeToCamel) as T;
      } else {
        return transformSnakeToCamel(responseData) as T;
      }
    }

    // Transform snake_case to camelCase for specific endpoints
    if (endpoint === "/admin/products" || endpoint.startsWith("/admin/products/") ||
        endpoint === "/admin/categories" || endpoint.startsWith("/admin/categories/")) {
      if (Array.isArray(responseData)) {
        return responseData.map(transformSnakeToCamel) as T;
      } else {
        return transformSnakeToCamel(responseData) as T;
      }
    }

    return responseData;
  } catch (error) {
    console.error(`Error en solicitud a ${endpoint}:`, error);
    throw error;
  }
}

// Helper function to transform snake_case to camelCase
function transformSnakeToCamel(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(transformSnakeToCamel);
  }

  return Object.keys(obj).reduce((result, key) => {
    // Transform key from snake_case to camelCase
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

    // Transform the value recursively if it's an object or array
    const value = obj[key];
    result[camelKey] = transformSnakeToCamel(value);

    return result;
  }, {} as any);
}

export const adminApi = {
  products: {
    getAll: async () => {
      console.log("Iniciando solicitud de obtener todos los productos");

      try {
        const result = await apiRequest<Product[]>("GET", "/admin/products");
        console.log("Productos obtenidos correctamente:", result.length);
        return result;
      } catch (error) {
        console.error("Error en products.getAll():", error);
        throw error;
      }
    },
    create: (product: NewProduct) => {
      // Convertir propiedades de camelCase a snake_case para el backend
      const dataToSend = {
        name: product.name,
        description: product.description,
        price: product.price,
        brand: product.brand,
        category_id: product.categoryId === null ? null : Number(product.categoryId),
        condition: product.condition,
        in_stock: product.inStock,
        images: product.images || [],
        features: product.features || [],
        technical_specs: product.technicalSpecs || [],
        video_url: product.videoUrl || null,
        pdf_url: product.pdfUrl || null
      };

      console.log("Enviando datos al API para crear producto:", dataToSend);
      console.log("Category ID en create:", dataToSend.category_id, "tipo:", typeof dataToSend.category_id);
      console.log("Technical specs en create:", dataToSend.technical_specs);
      console.log("Video URL en create:", dataToSend.video_url, "tipo:", typeof dataToSend.video_url);
      console.log("PDF URL en create:", dataToSend.pdf_url, "tipo:", typeof dataToSend.pdf_url);
      return apiRequest<Product>("POST", "/admin/products", dataToSend);
    },
    update: (id: number, product: Partial<Product>) => {
      // Convertir propiedades de camelCase a snake_case para el backend
      const dataToSend = {
        name: product.name,
        description: product.description,
        price: product.price,
        brand: product.brand,
        category_id: product.categoryId === null ? null : Number(product.categoryId),
        condition: product.condition,
        in_stock: product.inStock,
        images: product.images || [],
        features: product.features || [],
        technical_specs: product.technicalSpecs || [],
        // Enviar video_url explícitamente como null si no existe, para forzar la actualización
        video_url: product.videoUrl || null,
        pdf_url: product.pdfUrl || null
      };

      console.log("Enviando datos al API para actualizar producto:", dataToSend);
      console.log("Category ID en update:", dataToSend.category_id, "tipo:", typeof dataToSend.category_id);
      console.log("Technical specs en update:", dataToSend.technical_specs);
      console.log("Video URL en update:", dataToSend.video_url, "tipo:", typeof dataToSend.video_url);
      console.log("PDF URL en update:", dataToSend.pdf_url, "tipo:", typeof dataToSend.pdf_url);

      // Asegurarnos de que SIEMPRE se incluya video_url en la actualización
      const cleanDataToSend = {
        ...Object.fromEntries(
          Object.entries(dataToSend).filter(([key, value]) => {
            // Para otras propiedades, filtrar si son undefined
            return value !== undefined;
          })
        ),
        // Forzar la inclusión de video_url y category_id siempre
        video_url: dataToSend.video_url,
        pdf_url: dataToSend.pdf_url,
        category_id: dataToSend.category_id,
        technical_specs: dataToSend.technical_specs
      };

      console.log("Datos finales para actualizar producto:", cleanDataToSend);
      console.log("video_url en datos finales:", cleanDataToSend.video_url);
      console.log("pdf_url en datos finales:", cleanDataToSend.pdf_url);

      return apiRequest<Product>("PUT", `/admin/products/${id}`, cleanDataToSend);
    },
    delete: (id: number) => apiRequest("DELETE", `/admin/products/${id}`),
    uploadImage: async (file: File): Promise<string> => {
      try {
        // Verificar que el archivo sea una imagen
        if (!file.type.startsWith('image/')) {
          throw new Error('El archivo debe ser una imagen');
        }

        // Limitar el tamaño del archivo a 5MB
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
          throw new Error('La imagen no debe superar los 5MB');
        }

        // Crear un FormData para enviar el archivo
        const formData = new FormData();
        formData.append('file', file);

        console.log("Subiendo imagen con API_KEY fijo");

        // Hacer la petición a la Edge Function
        const response = await fetch(
          `${API_URL}/admin/upload-image`,
          {
            method: 'POST',
            body: formData,
            headers: {
              'Authorization': `Bearer ${API_KEY}`,
              // No incluir Content-Type para que el navegador establezca el boundary correcto
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Error al subir imagen: ${response.statusText}`);
        }

        const data = await response.json();

        // Procesar respuesta similar a como se hace en el código original
        let imageUrl;
        if (data.normalizedUrl) {
          imageUrl = data.normalizedUrl;
        } else if (data.directUrl) {
          imageUrl = data.directUrl;
        } else if (data.path) {
          imageUrl = `https://jlufgkgssnxumguvknhc.supabase.co/storage/v1/object/public/images/${data.path}`;
        } else if (data.url) {
          imageUrl = data.url;
        } else {
          throw new Error("No se recibió una URL válida del servidor");
        }

        console.log("Imagen subida correctamente:", imageUrl);
        return imageUrl;
      } catch (error: any) {
        console.error("Error en adminApi.products.uploadImage:", error);
        throw error;
      }
    },
    uploadVideo: async (file: File): Promise<string> => {
      try {
        // Validaciones (tipo, tamaño)
        if (!file.type.startsWith('video/')) {
          throw new Error("Selecciona un archivo de video.");
        }

        // Límite de 50MB
        if (file.size > 50 * 1024 * 1024) {
          throw new Error("Máximo 50MB para videos.");
        }

        // Crear un FormData para enviar el archivo
        const formData = new FormData();
        formData.append("file", file);

        console.log("Subiendo video con API_KEY fijo:", file.name);

        // Hacer la petición a la Edge Function
        const response = await fetch(
          `${API_URL}/admin/upload-video`,
          {
            method: "POST",
            body: formData,
            headers: {
              'Authorization': `Bearer ${API_KEY}`,
              // No incluir Content-Type para que el navegador establezca el boundary correcto
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          console.error("Error en respuesta de API (video):", errorData);
          throw new Error(errorData.message || errorData.error || "Error al subir el video");
        }

        const data = await response.json();
        console.log("Respuesta de subida de video:", data);

        if (!data.url || typeof data.url !== 'string') {
          throw new Error("No se recibió una URL válida del servidor para el video");
        }

        try {
          new URL(data.url);
          console.log("URL final de video validada:", data.url);
          return data.url;
        } catch (error) {
          console.error("URL de video inválida:", data.url);
          throw new Error("La URL de video recibida no es válida");
        }
      } catch (error: any) {
        console.error("Error al subir video:", error);
        throw error;
      }
    },
    uploadPdf: async (file: File): Promise<string> => {
      try {
        if (file.type !== 'application/pdf') {
          throw new Error("Selecciona un archivo PDF.");
        }
        if (file.size > 10 * 1024 * 1024) { // 10MB
          throw new Error("El PDF no debe superar los 10MB.");
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', 'fichas-tecnicas'); // Enviar la carpeta como un campo del form

        console.log("Subiendo PDF a marketplace-used/storage/upload");

        const response = await fetch(`${API_URL}/marketplace-used/storage/upload`, {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${API_KEY}`,
            // Ya no se necesita el header personalizado
          },
        });

        if (!response.ok) {
           const errorBody = await response.text();
          console.error("Error del servidor al subir PDF:", errorBody);
          throw new Error(`Error al subir PDF: ${response.statusText}`);
        }

        const data = await response.json();
        if (!data.path) { // La respuesta de esta ruta devuelve 'path'
          throw new Error("La respuesta del servidor no contenía una ruta de archivo válida.");
        }

        console.log("PDF subido correctamente, path:", data.path);
        // La URL pública se construye usando el bucket 'marketplace-used'
        const publicURL = `${SUPABASE_STORAGE_URL}/object/public/marketplace-used/${data.path}`;
        console.log("URL pública construida:", publicURL);
        return publicURL;

      } catch (error: any) {
        console.error("Error en adminApi.products.uploadPdf:", error);
        throw new Error(`Error al subir PDF: ${error.message}`);
      }
    },
  },
  categories: {
    getAll: () => apiRequest<Category[]>("GET", "/admin/categories"),
    getHierarchy: () => apiRequest<Category[]>("GET", "/admin/categories/hierarchy"),
    create: (category: NewCategory) => {
      // Convertir propiedades de camelCase a snake_case para el backend
      const dataToSend = {
        name: category.name,
        description: category.description,
        parent_id: category.parentId,
        gradient: category.gradient
      };

      console.log("Enviando datos al API para crear categoría:", dataToSend);
      return apiRequest<Category>("POST", "/admin/categories", dataToSend);
    },
    update: (id: number, category: NewCategory) => {
      // Convertir propiedades de camelCase a snake_case para el backend
      const dataToSend = {
        name: category.name,
        description: category.description,
        parent_id: category.parentId,
        gradient: category.gradient
      };

      console.log("Enviando datos al API para actualizar categoría:", id, dataToSend);
      return apiRequest<Category>("PUT", `/admin/categories/${id}`, dataToSend);
    },
    delete: (id: number) => apiRequest<{ success: true }>("DELETE", `/admin/categories/${id}`, { deleteProducts: true }),
    move: (categoryId: number, newParentId: number | null) =>
      apiRequest<{ success: true, message: string }>(
        "PUT",
        `/admin/categories/${categoryId}/move`,
        { newParentId }
      ),
  },
  scheduleCalls: {
    getAll: () => apiRequest<ScheduleCall[]>("GET", "/admin/schedule-calls"),
    updateStatus: (id: number, status: string) => apiRequest<ScheduleCall>("PUT", `/admin/schedule-calls/${id}/status`, { status }),
  },
  spareParts: {
    getAll: () => {
      return apiRequest<SparePart[]>("GET", "/admin/spare-parts");
    },
    create: (sparePart: Partial<SparePart>) => {
      const dataToSend = {
        name: sparePart.name,
        description: sparePart.description,
        price: sparePart.price,
        sku: sparePart.sku,
        manufacturer: sparePart.manufacturer,
        in_stock: sparePart.inStock,
        images: sparePart.images || [],
        technical_specs: sparePart.technicalSpecs || []
      };

      console.log("Creando recambio con datos:", dataToSend);
      return apiRequest<SparePart>("POST", "/admin/spare-parts", dataToSend);
    },
    update: (id: number, sparePart: Partial<SparePart>) => {
      const dataToSend = {
        name: sparePart.name,
        description: sparePart.description,
        price: sparePart.price,
        sku: sparePart.sku,
        manufacturer: sparePart.manufacturer,
        in_stock: sparePart.inStock,
        images: sparePart.images || [],
        technical_specs: sparePart.technicalSpecs || []
      };

      console.log("Actualizando recambio con ID:", id, "Datos:", dataToSend);
      return apiRequest<SparePart>("PUT", `/admin/spare-parts/${id}`, dataToSend);
    },
    delete: (id: number) => {
      console.log("Eliminando recambio con ID:", id);
      return apiRequest("DELETE", `/admin/spare-parts/${id}`);
    },

    // Endpoints para asignaciones de recambios a productos
    assignToProduct: (data: { sparePartId: number; productId: number; compatibilityNotes: string; isEssential: boolean }) => {
      const dataToSend = {
        spare_part_id: data.sparePartId,
        product_id: data.productId,
        compatibility_notes: data.compatibilityNotes,
        is_essential: data.isEssential
      };

      console.log("Asignando recambio a producto:", dataToSend);
      return apiRequest("POST", "/admin/product-spare-parts", dataToSend);
    },

    getAssignments: (sparePartId: number) => {
      console.log("Obteniendo asignaciones para recambio ID:", sparePartId);
      return apiRequest<ProductSparePart[]>("GET", `/admin/product-spare-parts/spare-part/${sparePartId}`);
    },

    removeAssignment: (assignmentId: number) => {
      console.log("Eliminando asignación ID:", assignmentId);
      return apiRequest("DELETE", `/admin/product-spare-parts/${assignmentId}`);
    },
  },
};
